import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { copyToClipboard } from '../utils/clipboard';

/* ===========================================================================
 * ShopifyIntegration V1 — Integração nativa Shopify
 *
 * - Estado desconectado: passo-a-passo didático + form de conexão
 * - Estado conectado:
 *     • Card da loja conectada (com badge de status)
 *     • Configurações (origem fixa, URL de rastreio, rota, auto-fulfill)
 *     • Lista de pedidos pagos não fulfilled (checkbox + sync em lote)
 *     • Histórico de sincronizações
 *
 * Backend: /api/shopify/{status,connect,config,disconnect,orders,sync,synced-orders}
 *
 * Mantém padrão visual de GenerateCode/BuyCredits/MyCodes (Lexend, laranja #f90).
 * ========================================================================= */

const ROUTE_OPTIONS = [
  { value: 'BR', label: 'BR — Nacional Brasil', emoji: '🇧🇷' },
  { value: 'CN', label: 'CN — China → Brasil', emoji: '🇨🇳' },
  { value: 'INT', label: 'INT — Internacional', emoji: '🌐' },
];

// Sites de rastreio já existentes — o usuário escolhe um dos dois (não digita
// a URL na mão) pra evitar erro de digitação/domínio errado.
const TRACKING_URL_OPTIONS = [
  { value: 'https://globaldriveshipping.com/track?code={code}', label: 'Global Drive Shipping (internacional)', emoji: '🌐' },
  { value: 'https://unicargalogistica.com.br/rastreios?code={code}', label: 'Unicarga Logística (nacional)', emoji: '🇧🇷' },
];

// A partir de quantos dias um pedido pendente é considerado "esquecido"
const STALE_DAYS_THRESHOLD = 3;

/** Chip clicável que copia o valor pro clipboard com 1 clique/toque (desktop e mobile). */
const CopyChip = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <button type="button" className={`sh-copy-chip ${copied ? 'is-copied' : ''}`} onClick={handleCopy}>
      <code>{label ? `${label}: ${value}` : value}</code>
      <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}></i>
    </button>
  );
};

const daysPending = (createdAt) => {
  if (!createdAt) return 0;
  const created = new Date(createdAt);
  if (isNaN(created.getTime())) return 0;
  return Math.floor((Date.now() - created.getTime()) / 86400000);
};

// Limite de lojas Shopify simultâneas por conta (espelha a regra fixa do backend).
const MAX_STORES = 10;

const ShopifyIntegration = () => {
  const { user, refreshUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]); // multi-loja
  const [selectedId, setSelectedId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const [syncedHistory, setSyncedHistory] = useState([]);

  const credits = user?.credits ?? 0;
  const integration = useMemo(
    () => integrations.find((i) => i.id === selectedId) || null,
    [integrations, selectedId]
  );

  /* ---------- Toast helper ---------- */
  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  }, []);

  /* ---------- Carrega status (TODAS as lojas) ---------- */
  const loadStatus = useCallback(async (preferId) => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/shopify/status');
      const list = r.data.integrations || [];
      setIntegrations(list);
      if (list.length === 0) {
        setSelectedId(null);
      } else if (preferId && list.some((i) => i.id === preferId)) {
        setSelectedId(preferId);
      } else if (!list.some((i) => i.id === selectedId)) {
        setSelectedId(list[0].id);
      }
      setAddingNew(list.length === 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar integração');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  /* ---------- Trata o retorno do fluxo OAuth (redirect da Shopify) ---------- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('shopify_connected');
    const oauthError = params.get('shopify_error');
    if (connected) {
      showToast('success', 'Loja conectada com sucesso via Shopify!');
      window.history.replaceState({}, '', window.location.pathname);
      loadStatus();
    } else if (oauthError) {
      showToast('error', oauthError);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Carrega pedidos da loja selecionada ---------- */
  const loadOrders = useCallback(async () => {
    if (!integration || !integration.connected) return;
    setLoadingOrders(true);
    setOrdersError('');
    try {
      const r = await api.get('/shopify/orders', { params: { integrationId: integration.id } });
      setOrders(r.data.orders || []);
    } catch (err) {
      setOrdersError(err.response?.data?.error || 'Erro ao listar pedidos');
    } finally {
      setLoadingOrders(false);
    }
  }, [integration]);

  /* ---------- Carrega histórico da loja selecionada ---------- */
  const loadHistory = useCallback(async () => {
    if (!integration || !integration.connected) return;
    try {
      const r = await api.get('/shopify/synced-orders', { params: { integrationId: integration.id } });
      setSyncedHistory(r.data.orders || []);
    } catch (err) {
      /* silent */
    }
  }, [integration]);

  useEffect(() => {
    setSelectedIds(new Set());
    setSyncResult(null);
    if (integration && integration.connected) {
      loadOrders();
      loadHistory();
    } else {
      setOrders([]);
      setSyncedHistory([]);
    }
  }, [integration, loadOrders, loadHistory]);

  /* ---------- Sync handler ---------- */
  const handleSync = async () => {
    if (selectedIds.size === 0) {
      showToast('warn', 'Selecione ao menos um pedido');
      return;
    }
    if (credits < selectedIds.size) {
      showToast('error', `Créditos insuficientes (você tem ${credits}, precisa de ${selectedIds.size})`);
      return;
    }
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await api.post('/shopify/sync', {
        integrationId: integration.id,
        orderIds: Array.from(selectedIds),
      }, { timeout: 60000 });
      setSyncResult(r.data);
      setSelectedIds(new Set());
      refreshUser();
      await loadOrders();
      await loadHistory();
      const okCount = r.data.results?.filter((x) => x.ok).length || 0;
      showToast('success', `${okCount} pedido(s) sincronizado(s) com sucesso`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao sincronizar pedidos';
      showToast('error', msg);
    } finally {
      setSyncing(false);
    }
  };

  /* ---------- Disconnect (só a loja selecionada — histórico é preservado) ---------- */
  const handleDisconnect = async () => {
    if (!integration) return;
    if (!window.confirm(`Tem certeza que deseja desconectar "${integration.store_name || integration.shop_domain}"? O histórico de pedidos/códigos dessa loja é mantido pra sempre — você pode reconectar depois.`)) return;
    try {
      await api.delete(`/shopify/disconnect/${integration.id}`);
      showToast('success', 'Loja desconectada');
      await loadStatus();
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao desconectar');
    }
  };

  /* ---------- Cancela uma tentativa de conexão nunca concluída (ficaria "pendente" pra sempre) ---------- */
  const cancelPending = async (intg, e) => {
    e.stopPropagation();
    if (!window.confirm(`Cancelar a tentativa de conexão de "${intg.store_name || intg.shop_domain}"? Como ela nunca chegou a conectar, não há pedidos/histórico pra perder.`)) return;
    try {
      await api.delete(`/shopify/disconnect/${intg.id}`);
      showToast('success', 'Tentativa cancelada');
      await loadStatus();
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao cancelar');
    }
  };

  /* ---------- Renomear loja direto na aba (sem abrir Configurações) ---------- */
  const startRename = (intg, e) => {
    e.stopPropagation();
    setRenamingId(intg.id);
    setRenameValue(intg.store_name || '');
  };

  const saveRename = async (intg) => {
    const name = renameValue.trim();
    setRenamingId(null);
    if (name === (intg.store_name || '')) return; // nada mudou
    try {
      await api.put('/shopify/config', { integrationId: intg.id, storeName: name });
      await loadStatus(intg.id);
      showToast('success', 'Nome da loja atualizado');
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao renomear');
    }
  };

  /* ===================================================================== */
  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{styles}</style>

      <div className="sh">
        {/* HEADER */}
        <header className="sh-header">
          <div className="sh-header-inner">
            <div className="sh-header-left">
              {/* Botão "Voltar" removido — navegação já fica na sidebar (ClientLayout) */}
              <div>
                <h1 className="sh-header-title">
                  <span className="sh-logo-shopify"><i className="fab fa-shopify"></i></span>
                  Integração Shopify
                </h1>
                <p className="sh-header-sub">
                  Importe pedidos pagos e gere rastreios automaticamente
                </p>
              </div>
            </div>
            <div className="sh-header-right">
              <div className={`sh-balance-chip ${credits === 0 ? 'empty' : credits < 10 ? 'low' : ''}`}>
                <i className="fas fa-coins"></i>
                <div className="sh-balance-info">
                  <span className="sh-balance-label">Créditos</span>
                  <span className="sh-balance-val">{credits}</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* TOAST */}
        {toast && (
          <div className={`sh-toast sh-toast-${toast.type}`}>
            <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : toast.type === 'error' ? 'fa-times-circle' : 'fa-exclamation-triangle'}`}></i>
            <span>{toast.msg}</span>
          </div>
        )}

        {/* MAIN */}
        <main className="sh-main">
          {error && (
            <div className="sh-alert sh-alert-error">
              <i className="fas fa-circle-exclamation"></i>
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="sh-card sh-loading">
              <div className="sh-spinner"></div>
              <p>Carregando integração…</p>
            </div>
          ) : (
            <>
              {/* SELETOR DE LOJAS (multi-loja, até MAX_STORES) */}
              {integrations.length > 0 && (
                <section className="sh-store-switcher">
                  {integrations.map((intg) => (
                    <div
                      key={intg.id}
                      className={`sh-store-tab ${!addingNew && selectedId === intg.id ? 'is-active' : ''}`}
                      onClick={() => { if (renamingId !== intg.id) { setAddingNew(false); setSelectedId(intg.id); } }}
                      role="button"
                      tabIndex={0}
                    >
                      {/* Indicador de saúde: vermelho = erro recente (7 dias), verde = ok, cinza = pendente */}
                      <span
                        className={`sh-health-dot ${!intg.connected ? 'pending' : intg.hasErrors ? 'error' : 'ok'}`}
                        title={!intg.connected ? 'Pendente de conexão' : intg.hasErrors ? 'Erros recentes — veja o histórico' : 'Sem erros recentes'}
                      ></span>
                      <i className="fab fa-shopify"></i>
                      {renamingId === intg.id ? (
                        <input
                          autoFocus
                          className="sh-store-tab-rename-input"
                          value={renameValue}
                          placeholder={intg.shop_domain}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onBlur={() => saveRename(intg)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveRename(intg); if (e.key === 'Escape') setRenamingId(null); }}
                          maxLength={60}
                        />
                      ) : (
                        <span className="sh-store-tab-name">{intg.store_name || intg.shop_domain}</span>
                      )}
                      {!intg.connected && <span className="sh-store-tab-pending">pendente</span>}
                      {renamingId !== intg.id && (
                        <button className="sh-store-tab-rename-btn" onClick={(e) => startRename(intg, e)} title="Renomear loja">
                          <i className="fas fa-pen"></i>
                        </button>
                      )}
                      {/* Tentativa de conexão nunca concluída (ficou "pendente" pra sempre, sem
                          token) — precisa de um jeito de remover, já que a tela de "Desconectar"
                          só existe pra lojas já conectadas. */}
                      {!intg.connected && (
                        <button
                          className="sh-store-tab-rename-btn sh-store-tab-cancel"
                          onClick={(e) => cancelPending(intg, e)}
                          title="Cancelar esta tentativa de conexão"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  ))}
                  {integrations.length < MAX_STORES && (
                    <button className={`sh-store-tab sh-store-tab-add ${addingNew ? 'is-active' : ''}`} onClick={() => setAddingNew(true)}>
                      <i className="fas fa-plus"></i> Conectar outra loja
                    </button>
                  )}
                  <span className="sh-store-count">{integrations.length}/{MAX_STORES} lojas</span>
                </section>
              )}

              {addingNew || !integration || !integration.connected ? (
                <DisconnectedView
                  onConnected={(intg) => {
                    loadStatus(intg.id);
                    showToast('success', 'Loja conectada com sucesso!');
                  }}
                  showToast={showToast}
                />
              ) : (
                <ConnectedView
                  integration={integration}
                  orders={orders}
                  loadingOrders={loadingOrders}
                  ordersError={ordersError}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                  syncing={syncing}
                  syncResult={syncResult}
                  syncedHistory={syncedHistory}
                  credits={credits}
                  onSync={handleSync}
                  onRefresh={loadOrders}
                  onDisconnect={handleDisconnect}
                  onUpdated={(intg) => {
                    loadStatus(intg.id);
                    showToast('success', 'Configurações salvas');
                  }}
                  showToast={showToast}
                />
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

/* ===================================================================== */
/* ESTADO: DESCONECTADO — Passo a passo + form de conexão                  */
/* ===================================================================== */
const DisconnectedView = ({ onConnected, showToast }) => {
  const [storeName, setStoreName] = useState('');
  const [installDomain, setInstallDomain] = useState('');
  const [installClientId, setInstallClientId] = useState('');
  const [installClientSecret, setInstallClientSecret] = useState('');
  const [defaultOrigin, setDefaultOrigin] = useState('');
  const [trackingUrlTemplate, setTrackingUrlTemplate] = useState(TRACKING_URL_OPTIONS[0].value);
  const [trackingCompany, setTrackingCompany] = useState('COMEX Logistics');
  const [routeType, setRouteType] = useState('BR');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [installing, setInstalling] = useState(false);
  const [installErrors, setInstallErrors] = useState({});
  const [showInstallSecret, setShowInstallSecret] = useState(false);

  const handleOAuthConnect = async (e) => {
    e.preventDefault();
    setInstallErrors({});
    const newErrors = {};
    if (!installDomain.trim()) newErrors.domain = 'Informe o domínio da loja';
    if (!installClientId.trim()) newErrors.clientId = 'Informe o Client ID do app';
    if (!installClientSecret.trim()) newErrors.clientSecret = 'Informe o Client Secret do app';
    if (!defaultOrigin.trim()) newErrors.defaultOrigin = 'Defina uma origem padrão';
    if (Object.keys(newErrors).length > 0) {
      setInstallErrors(newErrors);
      return;
    }
    setInstalling(true);
    try {
      const r = await api.post('/shopify/oauth/prepare', {
        storeName: storeName.trim(),
        shopDomain: installDomain.trim(),
        clientId: installClientId.trim(),
        clientSecret: installClientSecret.trim(),
        defaultOrigin: defaultOrigin.trim(),
        trackingUrlTemplate: trackingUrlTemplate.trim(),
        trackingCompany: trackingCompany.trim() || 'COMEX Logistics',
        routeType,
        notifyCustomer,
      });
      window.location.href = r.data.url;
    } catch (err) {
      setInstalling(false);
      const msg = err.response?.data?.error || 'Erro ao preparar conexão';
      setInstallErrors({ submit: msg });
      showToast('error', msg);
    }
  };

  return (
    <>
      {/* HERO + STEPS */}
      <section className="sh-hero">
        <div className="sh-hero-icon">
          <i className="fab fa-shopify"></i>
        </div>
        <h2 className="sh-hero-title">Conecte sua loja Shopify ao Painel COMEX</h2>
        <p className="sh-hero-sub">
          Importe pedidos pagos automaticamente, gere códigos de rastreio em massa e atualize o
          Shopify com o link de rastreamento — o cliente recebe e-mail automático.
        </p>

        <div className="sh-bullets">
          <div className="sh-bullet"><i className="fas fa-bolt"></i>Setup em 5 minutos</div>
          <div className="sh-bullet"><i className="fas fa-shield-halved"></i>Credenciais criptografadas (AES-256)</div>
          <div className="sh-bullet"><i className="fas fa-envelope"></i>E-mail automático ao cliente</div>
        </div>
      </section>

      {/* PASSO-A-PASSO */}
      <section className="sh-card sh-steps-card">
        <div className="sh-card-head">
          <div className="sh-card-head-icon">
            <i className="fas fa-list-check"></i>
          </div>
          <div>
            <h2 className="sh-card-title">Passo a passo — Como conectar</h2>
            <p className="sh-card-sub">Siga estas etapas na sua conta de desenvolvedor da Shopify (Dev Dashboard)</p>
          </div>
        </div>

        <ol className="sh-steps">
          <li>
            <span className="sh-step-num">1</span>
            <div className="sh-step-body">
              <strong>Acesse o Dev Dashboard</strong>
              <p>Entre em <CopyChip value="https://dev.shopify.com/dashboard" /> com sua conta (crie uma conta de Partners gratuita se ainda não tiver).</p>
            </div>
          </li>
          <li>
            <span className="sh-step-num">2</span>
            <div className="sh-step-body">
              <strong>Crie um app</strong>
              <p>Clique em <b>"Criar app"</b>, escolha <b>"Começar do Dev Dashboard"</b> e dê um nome (ex: "Painel COMEX").</p>
            </div>
          </li>
          <li>
            <span className="sh-step-num">3</span>
            <div className="sh-step-body">
              <strong>Configure a versão do app</strong>
              <p>Na aba de criar versão, preencha:</p>
              <div className="sh-scopes">
                <CopyChip label="URL do app" value="https://painelcomex.space" />
                <CopyChip label="URLs de redirecionamento" value="https://painelcomex.space/api/shopify/oauth/callback" />
                <CopyChip label="Escopos" value="read_orders,write_orders,read_merchant_managed_fulfillment_orders,write_merchant_managed_fulfillment_orders,read_third_party_fulfillment_orders,write_third_party_fulfillment_orders" />
              </div>
              <p className="sh-step-note">Toque em qualquer campo acima pra copiar. Deixe <b>"Incorporar app ao admin da Shopify"</b> desmarcado. Clique em <b>"Lançar"</b>.</p>
            </div>
          </li>
          <li>
            <span className="sh-step-num">4</span>
            <div className="sh-step-body">
              <strong>Copie o Client ID e o Client Secret</strong>
              <p>Vá em <b>Configurações → Credenciais</b> do app e copie os dois valores. Cole-os no formulário abaixo.</p>
            </div>
          </li>
          <li>
            <span className="sh-step-num">5</span>
            <div className="sh-step-body">
              <strong>Preencha e autorize na sua loja</strong>
              <p>Complete o formulário abaixo (credenciais + configurações dos códigos) e clique em conectar. Você será redirecionado pra Shopify pra autorizar o acesso à sua própria loja — confirme, e o painel COMEX conecta automaticamente.</p>
            </div>
          </li>
        </ol>
      </section>

      {/* FORM ÚNICO: CONEXÃO + CONFIGURAÇÕES */}
      <section className="sh-card">
        <div className="sh-card-head">
          <div className="sh-card-head-icon sh-icon-shopify">
            <i className="fab fa-shopify"></i>
          </div>
          <div>
            <h2 className="sh-card-title">Conectar agora</h2>
            <p className="sh-card-sub">Preencha os dados do app que você criou nos passos acima</p>
          </div>
        </div>
        <form className="sh-form" onSubmit={handleOAuthConnect} autoComplete="off">
          <input type="text" name="username" autoComplete="username" style={{ display: 'none' }} tabIndex="-1" aria-hidden="true" />
          <input type="password" name="password" autoComplete="new-password" style={{ display: 'none' }} tabIndex="-1" aria-hidden="true" />

          <div className="sh-field">
            <label className="sh-label">
              <i className="fas fa-tag"></i> Nome da loja (apelido, opcional)
            </label>
            <input
              type="text"
              className="sh-input"
              placeholder="Ex: Loja BR, Loja França..."
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              maxLength={60}
            />
            <small className="sh-hint">
              <i className="fas fa-info-circle"></i>
              Ajuda a identificar essa loja quando você tiver mais de uma conectada.
            </small>
          </div>

          <div className="sh-field">
            <label className="sh-label">
              <i className="fas fa-store"></i> Domínio da loja
              <span className="sh-required">*</span>
            </label>
            <input
              type="text"
              className={`sh-input ${installErrors.domain ? 'is-error' : ''}`}
              placeholder="sua-loja.myshopify.com"
              value={installDomain}
              onChange={(e) => setInstallDomain(e.target.value)}
              autoComplete="off"
              spellCheck="false"
            />
            <small className="sh-hint">
              <i className="fas fa-info-circle"></i>
              Use o domínio <code>.myshopify.com</code> (não o domínio personalizado).
            </small>
            {installErrors.domain && <small className="sh-error-text">{installErrors.domain}</small>}
          </div>

          <div className="sh-field">
            <label className="sh-label">
              <i className="fas fa-key"></i> Client ID
              <span className="sh-required">*</span>
            </label>
            <input
              type="text"
              className={`sh-input ${installErrors.clientId ? 'is-error' : ''}`}
              placeholder="Copiado das credenciais do seu app"
              value={installClientId}
              onChange={(e) => setInstallClientId(e.target.value)}
              autoComplete="off"
              data-lpignore="true"
              spellCheck="false"
            />
            {installErrors.clientId && <small className="sh-error-text">{installErrors.clientId}</small>}
          </div>

          <div className="sh-field">
            <label className="sh-label">
              <i className="fas fa-key"></i> Client Secret
              <span className="sh-required">*</span>
            </label>
            <div className="sh-input-row">
              <input
                type={showInstallSecret ? 'text' : 'password'}
                className={`sh-input ${installErrors.clientSecret ? 'is-error' : ''}`}
                placeholder="Copiado das credenciais do seu app"
                value={installClientSecret}
                onChange={(e) => setInstallClientSecret(e.target.value)}
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore="true"
                spellCheck="false"
              />
              <button type="button" className="sh-btn-icon" onClick={() => setShowInstallSecret((v) => !v)}>
                <i className={`fas ${showInstallSecret ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            <small className="sh-hint">
              <i className="fas fa-shield-halved"></i>
              Criptografado (AES-256-GCM) antes de ser salvo no banco.
            </small>
            {installErrors.clientSecret && <small className="sh-error-text">{installErrors.clientSecret}</small>}
          </div>

          <div className="sh-divider"><span>Configurações dos códigos</span></div>

          <div className="sh-field">
            <label className="sh-label">
              <i className="fas fa-warehouse"></i> Origem (fixa para todos os códigos)
              <span className="sh-required">*</span>
            </label>
            <input
              type="text"
              className={`sh-input ${installErrors.defaultOrigin ? 'is-error' : ''}`}
              placeholder="Ex: Centro de Distribuição São Paulo, SP"
              value={defaultOrigin}
              onChange={(e) => setDefaultOrigin(e.target.value)}
              maxLength={150}
            />
            <small className="sh-hint">
              <i className="fas fa-info-circle"></i>
              Essa é a origem que aparecerá em <b>todos</b> os códigos gerados via Shopify.
            </small>
            {installErrors.defaultOrigin && <small className="sh-error-text">{installErrors.defaultOrigin}</small>}
          </div>

          <div className="sh-field">
            <label className="sh-label">
              <i className="fas fa-link"></i> Site de rastreamento
            </label>
            <select
              className="sh-input sh-select"
              value={trackingUrlTemplate}
              onChange={(e) => setTrackingUrlTemplate(e.target.value)}
            >
              {TRACKING_URL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.emoji} {o.label}</option>
              ))}
            </select>
            <small className="sh-hint">
              <i className="fas fa-info-circle"></i>
              O cliente verá esse link clicável no e-mail enviado pela Shopify.
            </small>
          </div>

          <div className="sh-row">
            <div className="sh-field">
              <label className="sh-label">
                <i className="fas fa-truck"></i> Nome da transportadora
              </label>
              <input
                type="text"
                className="sh-input"
                placeholder="COMEX Logistics"
                value={trackingCompany}
                onChange={(e) => setTrackingCompany(e.target.value)}
                maxLength={50}
              />
            </div>

            <div className="sh-field">
              <label className="sh-label">
                <i className="fas fa-route"></i> Rota dos códigos
              </label>
              <select
                className="sh-input sh-select"
                value={routeType}
                onChange={(e) => setRouteType(e.target.value)}
              >
                {ROUTE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.emoji} {o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <label className="sh-toggle">
            <input
              type="checkbox"
              checked={notifyCustomer}
              onChange={(e) => setNotifyCustomer(e.target.checked)}
            />
            <span className="sh-toggle-track"><span className="sh-toggle-thumb" /></span>
            <span className="sh-toggle-label">
              <strong>Notificar o cliente por e-mail</strong>
              <small>A Shopify enviará o e-mail padrão "Shipping confirmation" com o link de rastreio.</small>
            </span>
          </label>

          {installErrors.submit && (
            <div className="sh-alert sh-alert-error">
              <i className="fas fa-circle-exclamation"></i>
              <span>{installErrors.submit}</span>
            </div>
          )}

          <button type="submit" className="sh-cta" disabled={installing}>
            {installing ? (
              <><span className="sh-spinner-sm"></span> Redirecionando…</>
            ) : (
              <><i className="fas fa-plug"></i> Conectar minha loja Shopify</>
            )}
          </button>

          <p className="sh-form-disclaimer">
            <i className="fas fa-lock"></i>
            Ao conectar, você concorda em compartilhar dados de pedidos com o Painel COMEX
            apenas para fins de geração de códigos de rastreio. Você pode desconectar a qualquer momento.
          </p>
        </form>
      </section>
    </>
  );
};

/* ===================================================================== */
/* ESTADO: CONECTADO — painel de gestão                                    */
/* ===================================================================== */
const ConnectedView = ({
  integration, orders, loadingOrders, ordersError, selectedIds, setSelectedIds,
  syncing, syncResult, syncedHistory, credits,
  onSync, onRefresh, onDisconnect, onUpdated, showToast,
}) => {
  const [showConfig, setShowConfig] = useState(false);

  const filteredOrders = useMemo(() => {
    // Mostra todos, mas marca os já processados visualmente
    return orders;
  }, [orders]);

  const toggleAll = () => {
    const eligible = filteredOrders.filter((o) => !o.synced || o.syncStatus !== 'success');
    if (selectedIds.size === eligible.length && eligible.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligible.map((o) => o.id)));
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const eligible = filteredOrders.filter((o) => !o.synced || o.syncStatus !== 'success');
  const allSelected = eligible.length > 0 && selectedIds.size === eligible.length;

  const staleOrders = eligible.filter((o) => daysPending(o.createdAt) >= STALE_DAYS_THRESHOLD);

  return (
    <>
      {/* PAINEL DA LOJA CONECTADA */}
      <section className="sh-connected">
        <div className="sh-connected-main">
          <div className="sh-connected-icon">
            <i className="fab fa-shopify"></i>
            <span className="sh-pulse-dot"></span>
          </div>
          <div className="sh-connected-info">
            <div className="sh-connected-label">Loja conectada{integration.store_name ? ` · ${integration.store_name}` : ''}</div>
            <div className="sh-connected-domain">{integration.shop_domain}</div>
            <div className="sh-connected-meta">
              <span><i className="fas fa-circle-check"></i> Ativa</span>
              <span><i className="fas fa-key"></i> Token: {integration.accessTokenMasked}</span>
              {integration.last_sync_at && (
                <span><i className="fas fa-clock-rotate-left"></i> Última sync: {new Date(integration.last_sync_at).toLocaleString('pt-BR')}</span>
              )}
            </div>
          </div>
        </div>
        <div className="sh-connected-actions">
          <button className="sh-btn-ghost" onClick={() => setShowConfig((v) => !v)}>
            <i className="fas fa-gear"></i> {showConfig ? 'Ocultar' : 'Configurações'}
          </button>
          <button className="sh-btn-ghost sh-btn-danger" onClick={onDisconnect}>
            <i className="fas fa-link-slash"></i> Desconectar
          </button>
        </div>
      </section>

      {/* CONFIGURAÇÕES (collapsable) */}
      {showConfig && (
        <ConfigPanel
          integration={integration}
          onUpdated={onUpdated}
          showToast={showToast}
          onClose={() => setShowConfig(false)}
        />
      )}

      {/* PEDIDOS */}
      <section className="sh-card">
        <div className="sh-card-head sh-card-head-sticky">
          <div className="sh-card-head-icon">
            <i className="fas fa-cart-shopping"></i>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="sh-card-title">Pedidos pagos</h2>
            <p className="sh-card-sub">
              {loadingOrders ? 'Carregando…' :
                `${filteredOrders.length} pedido(s) aguardando envio${selectedIds.size > 0 ? ` · ${selectedIds.size} selecionado(s)` : ''}`
              }
            </p>
          </div>
          <button className="sh-btn-icon" onClick={onRefresh} disabled={loadingOrders} title="Atualizar">
            <i className={`fas fa-arrows-rotate ${loadingOrders ? 'sh-spin' : ''}`}></i>
          </button>
        </div>

        {ordersError && (
          <div className="sh-alert sh-alert-error">
            <i className="fas fa-circle-exclamation"></i>
            <span>{ordersError}</span>
          </div>
        )}

        {staleOrders.length > 0 && (
          <div className="sh-alert sh-alert-warn">
            <i className="fas fa-clock"></i>
            <span>
              <b>{staleOrders.length} {staleOrders.length === 1 ? 'pedido' : 'pedidos'}</b> aguardando há mais de {STALE_DAYS_THRESHOLD} dias sem código gerado — o cliente final pode estar esperando o rastreio.
            </span>
          </div>
        )}

        {loadingOrders ? (
          <div className="sh-loading-line">
            <div className="sh-spinner-sm" /> Carregando pedidos…
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="sh-empty">
            <i className="fas fa-inbox"></i>
            <h3>Nenhum pedido pendente</h3>
            <p>Quando seus clientes finalizarem a compra na Shopify, eles aparecerão aqui automaticamente.</p>
            <button className="sh-btn-ghost" onClick={onRefresh}>
              <i className="fas fa-arrows-rotate"></i> Verificar novamente
            </button>
          </div>
        ) : (
          <>
            {/* TOOLBAR DE SELEÇÃO */}
            <div className="sh-orders-toolbar">
              <label className="sh-checkbox sh-checkbox-toolbar">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                <span className="sh-checkbox-box"><i className="fas fa-check"></i></span>
                <span>Selecionar todos ({eligible.length})</span>
              </label>
              <button
                className="sh-btn-primary"
                onClick={onSync}
                disabled={syncing || selectedIds.size === 0 || credits < selectedIds.size}
              >
                {syncing ? (
                  <><span className="sh-spinner-sm"></span> Sincronizando…</>
                ) : (
                  <>
                    <i className="fas fa-bolt"></i>
                    Gerar códigos & enviar para Shopify
                    {selectedIds.size > 0 && <span className="sh-badge-count">{selectedIds.size}</span>}
                  </>
                )}
              </button>
            </div>

            {selectedIds.size > 0 && credits < selectedIds.size && (
              <div className="sh-alert sh-alert-warn">
                <i className="fas fa-triangle-exclamation"></i>
                <span>Créditos insuficientes. Você tem <b>{credits}</b> e precisa de <b>{selectedIds.size}</b>.</span>
              </div>
            )}

            <div className="sh-orders-list">
              {filteredOrders.map((o) => {
                const checked = selectedIds.has(o.id);
                const disabled = o.synced && o.syncStatus === 'success';
                return (
                  <div
                    key={o.id}
                    className={`sh-order ${checked ? 'is-selected' : ''} ${disabled ? 'is-disabled' : ''} ${o.synced ? `sh-order-${o.syncStatus}` : ''}`}
                  >
                    <label className="sh-checkbox">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleOne(o.id)}
                      />
                      <span className="sh-checkbox-box"><i className="fas fa-check"></i></span>
                    </label>

                    <div className="sh-order-main">
                      <div className="sh-order-head">
                        <strong className="sh-order-name">{o.name}</strong>
                        <span className="sh-order-customer">{o.customer}</span>
                        {(!o.synced || o.syncStatus !== 'success') && daysPending(o.createdAt) >= STALE_DAYS_THRESHOLD && (
                          <span className="sh-status-pill sh-status-stale">
                            <i className="fas fa-clock"></i> {daysPending(o.createdAt)} dias aguardando
                          </span>
                        )}
                        {o.synced && (
                          <span className={`sh-status-pill sh-status-${o.syncStatus}`}>
                            {o.syncStatus === 'success' ? (
                              <><i className="fas fa-check-circle"></i> Sincronizado</>
                            ) : o.syncStatus === 'partial' ? (
                              <><i className="fas fa-circle-exclamation"></i> Parcial</>
                            ) : (
                              <><i className="fas fa-circle-xmark"></i> Erro</>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="sh-order-dest">
                        <i className="fas fa-location-dot"></i>
                        {o.destination || <span className="sh-muted">Sem endereço de entrega</span>}
                      </div>
                      {Array.isArray(o.lineItems) && o.lineItems.length > 0 && (
                        <div className="sh-order-dest">
                          <i className="fas fa-tag"></i>
                          {o.lineItems.map((li) => `${li.title}${li.quantity ? ` (x${li.quantity})` : ''}`).join(', ')}
                        </div>
                      )}
                      <div className="sh-order-meta">
                        <span><i className="fas fa-box"></i> {o.itemCount} item(s)</span>
                        {o.total && <span><i className="fas fa-money-bill"></i> {o.currency} {o.total}</span>}
                        {o.email && <span><i className="fas fa-envelope"></i> {o.email}</span>}
                        {o.phone && <span><i className="fas fa-phone"></i> {o.phone}</span>}
                        <span><i className="fas fa-calendar"></i> {new Date(o.createdAt).toLocaleDateString('pt-BR')}</span>
                        {o.trackingCode && (
                          <span className="sh-tracking-pill">
                            <i className="fas fa-truck-fast"></i> {o.trackingCode}
                          </span>
                        )}
                      </div>
                      {o.syncError && (
                        <div className="sh-order-error">
                          <i className="fas fa-circle-exclamation"></i> {o.syncError}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      {/* RESULTADO DA SYNC */}
      {syncResult && syncResult.results && (
        <SyncResultPanel result={syncResult} onClose={() => { /* mantém visível até próxima sync */ }} />
      )}

      {/* HISTÓRICO */}
      {syncedHistory.length > 0 && (
        <HistoryPanel history={syncedHistory} />
      )}
    </>
  );
};

/* ===================================================================== */
/* CONFIG PANEL                                                            */
/* ===================================================================== */
const ConfigPanel = ({ integration, onUpdated, showToast, onClose }) => {
  const [defaultOrigin, setDefaultOrigin] = useState(integration.default_origin || '');
  const [trackingUrlTemplate, setTrackingUrlTemplate] = useState(integration.tracking_url_template || TRACKING_URL_OPTIONS[0].value);
  const [storeName, setStoreName] = useState(integration.store_name || '');
  const [trackingCompany, setTrackingCompany] = useState(integration.tracking_company || 'COMEX Logistics');
  const [routeType, setRouteType] = useState(integration.route_type || 'BR');
  const [notifyCustomer, setNotifyCustomer] = useState(!!integration.notify_customer);
  const [autoFulfill, setAutoFulfill] = useState(!!integration.auto_fulfill);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put('/shopify/config', {
        integrationId: integration.id,
        storeName: storeName.trim(),
        defaultOrigin: defaultOrigin.trim(),
        trackingUrlTemplate: trackingUrlTemplate.trim(),
        trackingCompany: trackingCompany.trim() || 'COMEX Logistics',
        routeType,
        notifyCustomer,
        autoFulfill,
      });
      onUpdated(r.data.integration);
      onClose();
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="sh-card">
      <div className="sh-card-head">
        <div className="sh-card-head-icon">
          <i className="fas fa-gear"></i>
        </div>
        <div>
          <h2 className="sh-card-title">Configurações da integração</h2>
          <p className="sh-card-sub">Aplicado em todos os códigos gerados via Shopify</p>
        </div>
      </div>

      <form className="sh-form" onSubmit={save}>
        <div className="sh-field">
          <label className="sh-label">
            <i className="fas fa-tag"></i> Nome da loja (apelido)
          </label>
          <input
            type="text"
            className="sh-input"
            placeholder="Ex: Loja BR, Loja França..."
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            maxLength={60}
          />
        </div>

        <div className="sh-field">
          <label className="sh-label">
            <i className="fas fa-warehouse"></i> Origem fixa
          </label>
          <input
            type="text"
            className="sh-input"
            placeholder="Ex: Centro de Distribuição São Paulo, SP"
            value={defaultOrigin}
            onChange={(e) => setDefaultOrigin(e.target.value)}
            maxLength={150}
          />
        </div>

        <div className="sh-field">
          <label className="sh-label">
            <i className="fas fa-link"></i> Site de rastreamento
          </label>
          <select
            className="sh-input sh-select"
            value={trackingUrlTemplate}
            onChange={(e) => setTrackingUrlTemplate(e.target.value)}
          >
            {TRACKING_URL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.emoji} {o.label}</option>
            ))}
          </select>
          <small className="sh-hint">
            <i className="fas fa-info-circle"></i>
            O cliente verá esse link clicável no e-mail enviado pela Shopify.
          </small>
        </div>

        <div className="sh-row">
          <div className="sh-field">
            <label className="sh-label">
              <i className="fas fa-truck"></i> Transportadora
            </label>
            <input
              type="text"
              className="sh-input"
              value={trackingCompany}
              onChange={(e) => setTrackingCompany(e.target.value)}
              maxLength={50}
            />
          </div>
          <div className="sh-field">
            <label className="sh-label">
              <i className="fas fa-route"></i> Rota
            </label>
            <select className="sh-input sh-select" value={routeType} onChange={(e) => setRouteType(e.target.value)}>
              {ROUTE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.emoji} {o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <label className="sh-toggle">
          <input type="checkbox" checked={autoFulfill} onChange={(e) => setAutoFulfill(e.target.checked)} />
          <span className="sh-toggle-track"><span className="sh-toggle-thumb" /></span>
          <span className="sh-toggle-label">
            <strong>Atualizar Shopify automaticamente</strong>
            <small>Cria fulfillment com o tracking_number assim que o código for gerado.</small>
          </span>
        </label>

        <label className="sh-toggle">
          <input type="checkbox" checked={notifyCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} />
          <span className="sh-toggle-track"><span className="sh-toggle-thumb" /></span>
          <span className="sh-toggle-label">
            <strong>Notificar cliente por e-mail</strong>
            <small>Dispara o e-mail "Shipping confirmation" padrão da Shopify.</small>
          </span>
        </label>

        <div className="sh-form-actions">
          <button type="button" className="sh-btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="sh-btn-primary" disabled={saving}>
            {saving ? (<><span className="sh-spinner-sm"></span> Salvando…</>) : (<><i className="fas fa-check"></i> Salvar configurações</>)}
          </button>
        </div>
      </form>
    </section>
  );
};

/* ===================================================================== */
/* SYNC RESULT PANEL                                                       */
/* ===================================================================== */
const SyncResultPanel = ({ result }) => {
  const ok = result.results.filter((r) => r.ok).length;
  const partial = result.results.filter((r) => r.partial).length;
  const failed = result.results.filter((r) => !r.ok && !r.partial).length;

  return (
    <section className="sh-card sh-result">
      <div className="sh-card-head">
        <div className="sh-card-head-icon sh-icon-success">
          <i className="fas fa-clipboard-check"></i>
        </div>
        <div>
          <h2 className="sh-card-title">Resultado da sincronização</h2>
          <p className="sh-card-sub">
            {result.codesCreated} código(s) gerado(s) · Saldo: {result.remainingCredits} crédito(s)
          </p>
        </div>
      </div>

      <div className="sh-result-stats">
        <div className="sh-stat sh-stat-success">
          <i className="fas fa-circle-check"></i>
          <div>
            <strong>{ok}</strong>
            <span>Sucesso</span>
          </div>
        </div>
        {partial > 0 && (
          <div className="sh-stat sh-stat-warn">
            <i className="fas fa-circle-exclamation"></i>
            <div>
              <strong>{partial}</strong>
              <span>Parcial (código criado, fulfillment falhou)</span>
            </div>
          </div>
        )}
        {failed > 0 && (
          <div className="sh-stat sh-stat-error">
            <i className="fas fa-circle-xmark"></i>
            <div>
              <strong>{failed}</strong>
              <span>Falhou</span>
            </div>
          </div>
        )}
      </div>

      <div className="sh-result-list">
        {result.results.map((r, i) => (
          <div key={i} className={`sh-result-row ${r.ok ? 'ok' : r.partial ? 'partial' : 'fail'}`}>
            <i className={`fas ${r.ok ? 'fa-check-circle' : r.partial ? 'fa-circle-exclamation' : 'fa-times-circle'}`}></i>
            <div className="sh-result-info">
              <div className="sh-result-title">
                {r.orderName || `Pedido ${r.orderId}`} {r.customer && <span>— {r.customer}</span>}
              </div>
              {r.tracking && <code>{r.tracking}</code>}
              {r.error && <small className="sh-result-error">{r.error}</small>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ===================================================================== */
/* HISTORY PANEL                                                           */
/* ===================================================================== */
const HistoryPanel = ({ history }) => (
  <section className="sh-card">
    <div className="sh-card-head">
      <div className="sh-card-head-icon">
        <i className="fas fa-clock-rotate-left"></i>
      </div>
      <div>
        <h2 className="sh-card-title">Histórico de sincronizações</h2>
        <p className="sh-card-sub">Últimos {history.length} pedidos processados via Shopify</p>
      </div>
    </div>

    <div className="sh-history-list">
      {history.slice(0, 20).map((h) => (
        <div key={h.id} className={`sh-history-row sh-status-${h.status}`}>
          <div className="sh-history-left">
            <strong>{h.shopify_order_name || `Pedido ${h.shopify_order_id}`}</strong>
            <span>{h.customer_name}{h.customer_phone ? ` • ${h.customer_phone}` : ''}</span>
          </div>
          <div className="sh-history-mid">
            {h.tracking_code && <code>{h.tracking_code}</code>}
            <small>{new Date(h.synced_at).toLocaleString('pt-BR')}</small>
          </div>
          <span className={`sh-status-pill sh-status-${h.status}`}>
            {h.status === 'success' ? (
              <><i className="fas fa-check-circle"></i> Sucesso</>
            ) : h.status === 'partial' ? (
              <><i className="fas fa-circle-exclamation"></i> Parcial</>
            ) : (
              <><i className="fas fa-circle-xmark"></i> Erro</>
            )}
          </span>
        </div>
      ))}
    </div>
  </section>
);

/* ===================================================================== */
/* STYLES                                                                  */
/* ===================================================================== */
const styles = `
  .sh, .sh *, .sh *::before, .sh *::after { margin: 0; padding: 0; box-sizing: border-box; }
  .sh {
    font-family: 'Lexend', sans-serif;
    background: linear-gradient(180deg, #f5f5f7 0%, #ececef 100%);
    min-height: 100vh; color: #333;
    -webkit-font-smoothing: antialiased; overflow-x: hidden;
  }

  /* SELETOR DE LOJAS (multi-loja) */
  .sh-store-switcher {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    margin-bottom: 16px; padding: 10px; background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08); border-radius: 14px;
    overflow-x: auto; -webkit-overflow-scrolling: touch;
  }
  .sh-store-tab {
    display: flex; align-items: center; gap: 6px; padding: 8px 14px;
    border-radius: 10px; border: 1px solid rgba(255,255,255,0.1);
    background: rgba(255,255,255,0.04); color: inherit; cursor: pointer;
    font-family: 'Lexend', sans-serif; font-size: 13px; white-space: nowrap; flex-shrink: 0;
  }
  .sh-store-tab.is-active { background: #ff9900; color: #1a1a2e; border-color: #ff9900; font-weight: 600; }
  .sh-store-tab-pending { font-size: 10px; opacity: 0.7; }
  .sh-store-tab-add { border-style: dashed; }
  .sh-health-dot {
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  }
  .sh-health-dot.ok { background: #22c55e; box-shadow: 0 0 0 2px rgba(34,197,94,0.25); }
  .sh-health-dot.error { background: #ef4444; box-shadow: 0 0 0 2px rgba(239,68,68,0.25); }
  .sh-health-dot.pending { background: #94a3b8; }
  .sh-store-tab-rename-btn {
    background: none; border: none; color: inherit; opacity: 0.55; cursor: pointer;
    padding: 2px 4px; font-size: 11px; flex-shrink: 0;
  }
  .sh-store-tab-rename-btn:hover { opacity: 1; }
  .sh-store-tab-cancel:hover { color: #ef4444; }
  .sh-store-tab-rename-input {
    background: rgba(0,0,0,0.15); border: 1px solid rgba(255,255,255,0.3); border-radius: 6px;
    color: inherit; font-size: 13px; font-family: 'Lexend', sans-serif; padding: 3px 6px; width: 110px;
  }
  .sh-store-count { margin-left: auto; font-size: 12px; opacity: 0.6; white-space: nowrap; flex-shrink: 0; }
  @media (max-width: 640px) {
    .sh-store-switcher { justify-content: flex-start; }
    .sh-store-count { margin-left: 0; width: 100%; order: 10; }
  }

  /* HEADER */
  .sh-header {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 100;
  }
  .sh-header-inner {
    max-width: 1280px; margin: 0 auto; padding: 14px 24px;
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
  }
  .sh-header-left { display: flex; align-items: center; gap: 14px; }
  .sh-back-btn {
    width: 40px; height: 40px; border-radius: 10px;
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.10);
    color: #f90; cursor: pointer; transition: all .2s;
    display: flex; align-items: center; justify-content: center; font-size: 16px;
  }
  .sh-back-btn:hover { background: rgba(255,153,0,0.15); transform: translateX(-2px); }
  .sh-header-title {
    font-size: 19px; font-weight: 700; color: #fff; line-height: 1.1;
    display: flex; align-items: center; gap: 10px;
  }
  .sh-logo-shopify {
    width: 28px; height: 28px; border-radius: 7px;
    background: linear-gradient(135deg, #95BF47, #5E8E3E);
    color: #fff; display: inline-flex; align-items: center; justify-content: center;
    font-size: 15px;
  }
  .sh-header-sub { font-size: 12px; color: rgba(255,255,255,0.55); margin-top: 2px; }
  .sh-header-right { display: flex; align-items: center; gap: 12px; }
  .sh-balance-chip {
    display: flex; align-items: center; gap: 12px;
    background: rgba(255,153,0,0.13); border: 1px solid rgba(255,153,0,0.30);
    padding: 8px 16px; border-radius: 12px; color: #fff;
  }
  .sh-balance-chip i { color: #f90; font-size: 18px; }
  .sh-balance-chip.low { background: rgba(245,158,11,0.18); border-color: rgba(245,158,11,0.50); }
  .sh-balance-chip.empty { background: rgba(229,62,62,0.18); border-color: rgba(229,62,62,0.50); }
  .sh-balance-info { display: flex; flex-direction: column; line-height: 1.1; }
  .sh-balance-label { font-size: 10px; color: rgba(255,255,255,0.6); text-transform: uppercase; letter-spacing: .5px; }
  .sh-balance-val { font-size: 17px; font-weight: 700; }

  /* MAIN */
  .sh-main { max-width: 1280px; margin: 0 auto; padding: 28px 24px 60px; display: grid; gap: 22px; }

  /* TOAST */
  .sh-toast {
    position: fixed; top: 80px; left: 50%; transform: translateX(-50%);
    background: #1a1a2e; color: #fff; padding: 12px 22px; border-radius: 12px;
    display: flex; align-items: center; gap: 10px; z-index: 9999;
    box-shadow: 0 12px 40px rgba(0,0,0,0.25); font-weight: 500; font-size: 14px;
    animation: shToastIn .25s ease;
  }
  .sh-toast i { font-size: 18px; }
  .sh-toast-success i { color: #22c55e; }
  .sh-toast-error i { color: #ef4444; }
  .sh-toast-warn i { color: #f59e0b; }
  @keyframes shToastIn { from { opacity: 0; transform: translate(-50%, -10px); } to { opacity: 1; transform: translate(-50%, 0); } }

  /* HERO */
  .sh-hero {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    color: #fff; border-radius: 20px; padding: 40px 30px; text-align: center;
    position: relative; overflow: hidden;
    box-shadow: 0 10px 40px rgba(0,0,0,0.15);
  }
  .sh-hero::before {
    content: ''; position: absolute; top: -50%; right: -20%;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(149,191,71,0.18), transparent 70%);
  }
  .sh-hero > * { position: relative; z-index: 1; }
  .sh-hero-icon {
    width: 72px; height: 72px; margin: 0 auto 20px;
    background: linear-gradient(135deg, #95BF47, #5E8E3E);
    border-radius: 20px; display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 36px;
    box-shadow: 0 12px 30px rgba(149,191,71,0.40);
  }
  .sh-hero-title { font-size: 28px; font-weight: 800; margin-bottom: 12px; letter-spacing: -0.5px; }
  .sh-hero-sub {
    font-size: 15px; color: rgba(255,255,255,0.75); max-width: 640px; margin: 0 auto;
    line-height: 1.55;
  }
  .sh-bullets {
    display: flex; gap: 24px; justify-content: center; margin-top: 28px; flex-wrap: wrap;
  }
  .sh-bullet {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
    padding: 8px 16px; border-radius: 999px;
    font-size: 13px; color: rgba(255,255,255,0.90);
  }
  .sh-bullet i { color: #95BF47; }

  /* CARD */
  .sh-card {
    background: #fff; border: 1px solid rgba(0,0,0,0.04);
    border-radius: 20px; padding: 26px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  }
  .sh-card-head {
    display: flex; align-items: center; gap: 14px; margin-bottom: 22px;
  }
  .sh-card-head-sticky {
    position: sticky; top: 68px; background: #fff; z-index: 5; padding: 4px 0;
  }
  .sh-card-head-icon {
    width: 50px; height: 50px; border-radius: 14px;
    background: linear-gradient(135deg, #f90, #ff6600);
    color: #fff; display: inline-flex; align-items: center; justify-content: center;
    font-size: 20px; flex-shrink: 0;
    box-shadow: 0 4px 15px rgba(255,153,0,0.30);
  }
  .sh-card-head-icon.sh-icon-shopify {
    background: linear-gradient(135deg, #95BF47, #5E8E3E);
    box-shadow: 0 4px 15px rgba(149,191,71,0.40);
  }
  .sh-card-head-icon.sh-icon-success {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    box-shadow: 0 4px 15px rgba(34,197,94,0.30);
  }
  .sh-card-title { font-size: 19px; font-weight: 700; line-height: 1.2; color: #222; }
  .sh-card-sub { font-size: 13px; color: #888; margin-top: 4px; }

  .sh-loading {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 60px 20px; gap: 16px; color: #64748b;
  }
  .sh-loading-line { display: flex; align-items: center; gap: 10px; color: #64748b; font-size: 14px; padding: 14px 0; justify-content: center; }

  /* ALERTS */
  .sh-alert {
    display: flex; align-items: center; gap: 12px;
    border-radius: 12px; padding: 14px 16px; font-size: 14px; margin-bottom: 16px;
  }
  .sh-alert-error { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
  .sh-alert-warn { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }

  /* STEPS (passo-a-passo) */
  .sh-steps-card { padding-bottom: 30px; }
  .sh-steps { list-style: none; padding: 0; margin: 0; display: grid; gap: 22px; }
  .sh-steps li { display: flex; gap: 16px; align-items: flex-start; }
  .sh-step-num {
    width: 36px; height: 36px; border-radius: 50%;
    background: linear-gradient(135deg, #f90, #ff6600);
    color: #fff; display: inline-flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 800; flex-shrink: 0;
    box-shadow: 0 6px 14px rgba(255,153,0,.35);
  }
  .sh-step-body { flex: 1; min-width: 0; }
  .sh-step-body strong { display: block; font-size: 16px; color: #222; margin-bottom: 6px; }
  .sh-step-body p { font-size: 14px; color: #555; line-height: 1.6; }
  .sh-step-body p + p { margin-top: 6px; }
  .sh-step-body code {
    background: #f8f9fa; color: #c2410c; padding: 2px 8px; border-radius: 6px;
    font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 13px;
    border: 1px solid #efefef;
  }
  .sh-step-body b { color: #222; }
  .sh-step-body u { text-decoration: none; border-bottom: 2px solid #f90; padding-bottom: 1px; }
  .sh-scopes {
    display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 0;
  }
  .sh-scopes code {
    font-size: 12px; padding: 4px 9px; background: #fff5e6; color: #c2410c;
    border: 1px solid #fed7aa; border-radius: 6px; font-weight: 600;
    word-break: break-all; overflow-wrap: anywhere; white-space: normal; max-width: 100%;
  }
  .sh-copy-chip {
    display: inline-flex; align-items: center; gap: 8px; vertical-align: middle;
    border: 1px solid #fed7aa; border-radius: 6px; background: none; padding: 0;
    cursor: pointer; font-family: inherit; transition: all .15s;
    max-width: 100%;
  }
  .sh-copy-chip:hover { background: #fff5e6; transform: translateY(-1px); }
  .sh-copy-chip code {
    border: 0; background: transparent;
    /* URLs longas (ex: https://painelcomex.space/api/shopify/oauth/callback)
       são uma "palavra" só sem espaço — sem isso, nunca quebram linha e
       empurram a página inteira pro lado no mobile. */
    word-break: break-all; overflow-wrap: anywhere; white-space: normal;
  }
  .sh-copy-chip i {
    font-size: 11px; color: #c2410c; padding-right: 9px; flex-shrink: 0;
  }
  .sh-copy-chip.is-copied { border-color: #22c55e; }
  .sh-copy-chip.is-copied i { color: #22c55e; }
  .sh-copy-chip.is-copied code { color: #22c55e; }
  .sh-step-note { font-size: 13px !important; color: #777 !important; font-style: italic; }
  .sh-step-warn {
    display: flex; gap: 10px; align-items: flex-start;
    background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px;
    padding: 12px 14px; margin-top: 10px; font-size: 13px; color: #92400e;
  }
  .sh-step-warn i { font-size: 18px; flex-shrink: 0; margin-top: 2px; color: #f59e0b; }

  /* FORM */
  .sh-form { display: grid; gap: 18px; }
  .sh-field { display: grid; gap: 6px; }
  .sh-label {
    font-size: 13px; font-weight: 700; color: #444;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .sh-label i { color: #f90; font-size: 13px; width: 14px; }
  .sh-required { color: #ef4444; }
  .sh-input {
    width: 100%; height: 46px; padding: 0 14px;
    border: 2px solid #e0e0e0; border-radius: 10px;
    font-size: 14px; font-family: inherit; color: #222; background: #fff;
    outline: none; transition: all .2s;
  }
  .sh-input:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,.15); }
  .sh-input.is-error { border-color: #e53e3e; box-shadow: 0 0 0 3px rgba(229,62,62,.12); }
  .sh-select { padding-right: 30px; cursor: pointer; appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23888' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 14px center;
  }
  .sh-input-row { display: flex; gap: 8px; align-items: stretch; }
  .sh-btn-icon {
    width: 46px; height: 46px; border-radius: 10px;
    background: #f8f9fa; border: 2px solid #e0e0e0; color: #666;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: all .2s; font-size: 15px; font-family: inherit;
    flex-shrink: 0;
  }
  .sh-btn-icon:hover { background: #fff5e6; border-color: #f90; color: #f90; }
  .sh-btn-icon:disabled { opacity: .6; cursor: not-allowed; }
  .sh-hint {
    font-size: 12px; color: #777; display: inline-flex; align-items: center; gap: 6px; line-height: 1.5;
  }
  .sh-hint i { color: #888; font-size: 11px; }
  .sh-hint code {
    background: #f8f9fa; color: #c2410c; padding: 1px 6px; border-radius: 4px;
    font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 11px;
  }
  .sh-error-text { font-size: 13px; color: #e53e3e; }
  .sh-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

  .sh-divider {
    display: flex; align-items: center; gap: 14px; margin: 8px 0;
    font-size: 12px; color: #999; text-transform: uppercase; letter-spacing: .5px; font-weight: 700;
  }
  .sh-divider::before, .sh-divider::after {
    content: ''; flex: 1; height: 1px; background: #ececec;
  }

  /* TOGGLE */
  .sh-toggle {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 16px; background: #f8f9fa; border: 1px solid #efefef; border-radius: 12px;
    cursor: pointer;
  }
  .sh-toggle input { display: none; }
  .sh-toggle-track {
    width: 44px; height: 24px; background: #ef4444; border-radius: 999px;
    position: relative; flex-shrink: 0; transition: background .2s;
    box-shadow: 0 2px 6px rgba(239, 68, 68, 0.3);
  }
  .sh-toggle-thumb {
    position: absolute; top: 3px; left: 3px;
    width: 18px; height: 18px; background: #fff; border-radius: 50%;
    transition: transform .2s; box-shadow: 0 1px 3px rgba(0,0,0,.2);
  }
  .sh-toggle input:checked + .sh-toggle-track { 
    background: #22c55e;
    box-shadow: 0 2px 6px rgba(34, 197, 94, 0.3);
  }
  .sh-toggle input:checked + .sh-toggle-track .sh-toggle-thumb { transform: translateX(20px); }
  .sh-toggle-label { display: flex; flex-direction: column; gap: 2px; }
  .sh-toggle-label strong { font-size: 14px; color: #222; }
  .sh-toggle-label small { font-size: 12px; color: #777; }

  /* CTA */
  .sh-cta {
    background: linear-gradient(135deg, #f90, #ff6600);
    color: #fff; border: 0; border-radius: 12px;
    padding: 15px 18px; font-size: 15px; font-weight: 700;
    cursor: pointer; transition: all .3s;
    display: inline-flex; align-items: center; justify-content: center; gap: 10px;
    box-shadow: 0 6px 20px rgba(255,153,0,.30);
    font-family: inherit;
  }
  .sh-cta:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(255,153,0,.40); }
  .sh-cta:disabled { opacity: .55; cursor: not-allowed; transform: none; box-shadow: none; }

  .sh-form-disclaimer {
    font-size: 12px; color: #999; text-align: center; line-height: 1.5;
    display: inline-flex; align-items: flex-start; gap: 6px; justify-content: center;
  }
  .sh-form-disclaimer i { color: #999; font-size: 11px; margin-top: 3px; }

  .sh-form-actions { display: flex; gap: 10px; justify-content: flex-end; padding-top: 6px; }

  /* CONNECTED */
  .sh-connected {
    background: linear-gradient(135deg, #fff, #fafbfc);
    border: 1px solid rgba(149,191,71,0.30); border-radius: 20px; padding: 22px 26px;
    display: flex; align-items: center; justify-content: space-between; gap: 20px;
    box-shadow: 0 6px 24px rgba(149,191,71,0.10);
  }
  .sh-connected-main { display: flex; align-items: center; gap: 16px; flex: 1; min-width: 0; }
  .sh-connected-icon {
    width: 56px; height: 56px; border-radius: 16px;
    background: linear-gradient(135deg, #95BF47, #5E8E3E);
    color: #fff; font-size: 24px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    position: relative;
    box-shadow: 0 6px 16px rgba(149,191,71,0.35);
  }
  .sh-pulse-dot {
    position: absolute; top: -2px; right: -2px;
    width: 12px; height: 12px; border-radius: 50%;
    background: #22c55e; border: 2px solid #fff;
    animation: shPulse 1.8s infinite;
  }
  @keyframes shPulse {
    0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34,197,94,.5); }
    50% { transform: scale(1.15); box-shadow: 0 0 0 8px rgba(34,197,94,0); }
  }
  .sh-connected-info { flex: 1; min-width: 0; }
  .sh-connected-label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: .5px; font-weight: 700; }
  .sh-connected-domain { font-size: 19px; font-weight: 700; color: #222; margin: 4px 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sh-connected-meta { display: flex; flex-wrap: wrap; gap: 16px; font-size: 12px; color: #666; }
  .sh-connected-meta i { color: #95BF47; margin-right: 4px; }
  .sh-connected-actions { display: flex; gap: 8px; flex-shrink: 0; }

  /* BUTTONS */
  .sh-btn-ghost {
    background: #fff; color: #555; border: 2px solid #e0e0e0;
    border-radius: 10px; padding: 9px 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: all .2s;
    display: inline-flex; align-items: center; gap: 8px;
    font-family: inherit; white-space: nowrap;
  }
  .sh-btn-ghost:hover { border-color: #f90; color: #f90; background: #fff5e6; }
  .sh-btn-ghost.sh-btn-danger:hover { border-color: #ef4444; color: #ef4444; background: #fef2f2; }
  .sh-btn-primary {
    background: linear-gradient(135deg, #f90, #ff6600);
    color: #fff; border: 0; border-radius: 10px;
    padding: 11px 18px; font-size: 14px; font-weight: 700;
    cursor: pointer; transition: all .25s;
    display: inline-flex; align-items: center; gap: 8px;
    font-family: inherit;
    box-shadow: 0 4px 14px rgba(255,153,0,.30);
  }
  .sh-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(255,153,0,.45); }
  .sh-btn-primary:disabled { opacity: .55; cursor: not-allowed; transform: none; box-shadow: none; }
  .sh-link-btn {
    background: none; border: 0; color: #9aa1ac; font-size: 13px;
    text-decoration: underline; cursor: pointer; padding: 10px 0 0; font-family: inherit;
  }
  .sh-link-btn:hover { color: #f90; }
  .sh-badge-count {
    background: rgba(255,255,255,.25); padding: 2px 9px; border-radius: 999px;
    font-size: 12px; font-weight: 800;
  }

  /* ORDERS TOOLBAR */
  .sh-orders-toolbar {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 14px 16px; background: #fff5e6; border: 1px solid #fed7aa; border-radius: 12px;
    margin-bottom: 14px;
  }

  /* CHECKBOX */
  .sh-checkbox { display: inline-flex; align-items: center; gap: 10px; cursor: pointer; user-select: none; font-size: 13px; color: #555; font-weight: 600; }
  .sh-checkbox input { display: none; }
  .sh-checkbox-box {
    width: 22px; height: 22px; border: 2px solid #d4d4d8; border-radius: 6px;
    display: inline-flex; align-items: center; justify-content: center;
    background: #fff; transition: all .15s; flex-shrink: 0;
  }
  .sh-checkbox-box i { color: #fff; font-size: 11px; opacity: 0; transition: opacity .15s; }
  .sh-checkbox input:checked + .sh-checkbox-box {
    background: #f90; border-color: #f90;
  }
  .sh-checkbox input:checked + .sh-checkbox-box i { opacity: 1; }
  .sh-checkbox input:disabled + .sh-checkbox-box {
    background: #f5f5f5; border-color: #d4d4d8; cursor: not-allowed;
  }
  .sh-checkbox-toolbar { font-weight: 700; color: #222; }

  /* ORDERS LIST */
  .sh-orders-list { display: grid; gap: 10px; }
  .sh-order {
    display: flex; gap: 14px; align-items: flex-start;
    background: #fafafa; border: 1.5px solid #efefef; border-radius: 14px;
    padding: 16px; transition: all .2s;
  }
  .sh-order:hover { background: #fff; border-color: #d4d4d8; }
  .sh-order.is-selected {
    background: linear-gradient(135deg, #fff5e6, #ffedd5);
    border-color: #f90; box-shadow: 0 6px 18px rgba(255,153,0,.15);
  }
  .sh-order.is-disabled { opacity: .65; }
  .sh-order-success { border-left: 4px solid #22c55e; }
  .sh-order-partial { border-left: 4px solid #f59e0b; }
  .sh-order-failed { border-left: 4px solid #ef4444; }

  .sh-order-main { flex: 1; min-width: 0; display: grid; gap: 6px; }
  .sh-order-head { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
  .sh-order-name { font-size: 15px; color: #222; font-weight: 700; }
  .sh-order-customer { font-size: 13px; color: #666; }
  .sh-order-dest {
    font-size: 13px; color: #555; line-height: 1.4;
    display: flex; align-items: flex-start; gap: 6px;
  }
  .sh-order-dest i { color: #f90; margin-top: 3px; flex-shrink: 0; }
  .sh-order-meta {
    display: flex; gap: 14px; flex-wrap: wrap;
    font-size: 12px; color: #888; padding-top: 2px;
  }
  .sh-order-meta i { color: #aaa; margin-right: 4px; }
  .sh-tracking-pill {
    background: #f0fdf4; color: #16a34a !important; padding: 2px 9px; border-radius: 6px;
    font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 11.5px; font-weight: 700;
    border: 1px solid #bbf7d0;
  }
  .sh-tracking-pill i { color: #16a34a !important; }
  .sh-order-error {
    margin-top: 6px; padding: 8px 12px; background: #fef2f2; border: 1px solid #fecaca;
    border-radius: 8px; color: #b91c1c; font-size: 12px;
    display: flex; align-items: center; gap: 8px;
  }

  /* STATUS PILLS */
  .sh-status-pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 20px;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .3px;
  }
  .sh-status-pill i { font-size: 10px; }
  .sh-status-success, .sh-status-pill.sh-status-success { background: #f0fdf4; color: #16a34a; }
  .sh-status-partial, .sh-status-pill.sh-status-partial { background: #fffbeb; color: #92400e; }
  .sh-status-failed, .sh-status-pill.sh-status-failed { background: #fef2f2; color: #b91c1c; }
  .sh-status-pending { background: #f1f5f9; color: #475569; }
  .sh-status-pill.sh-status-stale { background: #fef2f2; color: #b91c1c; animation: sh-stale-pulse 2s ease-in-out infinite; }
  @keyframes sh-stale-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }

  /* EMPTY */
  .sh-empty {
    text-align: center; padding: 50px 20px; color: #888;
  }
  .sh-empty i { font-size: 48px; color: #d4d4d8; margin-bottom: 14px; display: block; }
  .sh-empty h3 { font-size: 18px; color: #222; margin-bottom: 8px; }
  .sh-empty p { font-size: 14px; color: #777; margin-bottom: 18px; max-width: 380px; margin-left: auto; margin-right: auto; }
  .sh-muted { color: #aaa; font-style: italic; }

  /* RESULT */
  .sh-result-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 18px; }
  .sh-result-stats .sh-stat {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 16px; border-radius: 12px; background: #f8f9fa; border: 1px solid #efefef;
  }
  .sh-stat strong { display: block; font-size: 22px; font-weight: 800; color: #222; }
  .sh-stat span { font-size: 11px; color: #777; }
  .sh-stat i { font-size: 26px; }
  .sh-stat-success i { color: #22c55e; }
  .sh-stat-warn i { color: #f59e0b; }
  .sh-stat-error i { color: #ef4444; }

  .sh-result-list { display: grid; gap: 8px; }
  .sh-result-row {
    display: flex; align-items: center; gap: 12px;
    padding: 10px 14px; background: #f8f9fa; border-radius: 10px; font-size: 13px;
  }
  .sh-result-row.ok { border-left: 3px solid #22c55e; }
  .sh-result-row.partial { border-left: 3px solid #f59e0b; }
  .sh-result-row.fail { border-left: 3px solid #ef4444; }
  .sh-result-row > i { font-size: 18px; flex-shrink: 0; }
  .sh-result-row.ok > i { color: #22c55e; }
  .sh-result-row.partial > i { color: #f59e0b; }
  .sh-result-row.fail > i { color: #ef4444; }
  .sh-result-info { flex: 1; min-width: 0; }
  .sh-result-title { font-weight: 700; color: #222; font-size: 13px; }
  .sh-result-title span { font-weight: 400; color: #666; }
  .sh-result-info code {
    display: inline-block; margin-top: 2px;
    font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 12px;
    color: #c2410c; background: #fff5e6; padding: 1px 8px; border-radius: 4px;
  }
  .sh-result-error { display: block; margin-top: 3px; color: #b91c1c; font-size: 12px; }

  /* HISTORY */
  .sh-history-list { display: grid; gap: 8px; }
  .sh-history-row {
    display: grid; grid-template-columns: 1.2fr 1.5fr auto; gap: 14px; align-items: center;
    background: #f8f9fa; border: 1px solid #efefef; border-radius: 10px;
    padding: 12px 14px; transition: all .2s;
  }
  .sh-history-row:hover { background: #fff; border-color: #e0e0e0; }
  .sh-history-row.sh-status-success { border-left: 3px solid #22c55e; }
  .sh-history-row.sh-status-partial { border-left: 3px solid #f59e0b; }
  .sh-history-row.sh-status-failed { border-left: 3px solid #ef4444; }
  .sh-history-left strong { display: block; font-size: 13px; color: #222; font-weight: 700; }
  .sh-history-left span { font-size: 12px; color: #777; }
  .sh-history-mid { display: flex; flex-direction: column; gap: 2px; }
  .sh-history-mid code {
    font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 12px;
    color: #c2410c; background: #fff5e6; padding: 1px 8px; border-radius: 4px; width: max-content;
  }
  .sh-history-mid small { font-size: 11px; color: #888; }

  /* SPINNER */
  .sh-spinner {
    width: 40px; height: 40px; border-radius: 50%;
    border: 3px solid #f0f0f0; border-top-color: #f90;
    animation: shSpin .8s linear infinite;
  }
  .sh-spinner-sm {
    width: 16px; height: 16px; border-radius: 50%;
    border: 2px solid rgba(255,255,255,.4); border-top-color: #fff;
    animation: shSpin .8s linear infinite;
  }
  .sh-spin { animation: shSpin 1.2s linear infinite; }
  @keyframes shSpin { to { transform: rotate(360deg); } }

  /* ============= RESPONSIVE ============= */
  /* Monitor estreito / vertical (~1080px): linhas e formulários respiram */
  @media (max-width: 1280px) {
    .sh-row { grid-template-columns: 1fr; }
    .sh-connected { flex-direction: column; align-items: flex-start; }
    .sh-connected-actions { width: 100%; justify-content: flex-end; }
    .sh-result-stats { grid-template-columns: 1fr; }
    .sh-history-row { grid-template-columns: 1fr; gap: 6px; }
  }
  @media (max-width: 720px) {
    .sh-header-inner { padding: 12px 16px; flex-wrap: wrap; gap: 10px; }
    .sh-header-title { font-size: 17px; }
    .sh-header-sub { display: none; }
    .sh-header-right { width: 100%; justify-content: flex-end; }
    .sh-main { padding: 22px 14px 50px; }
    .sh-card { padding: 22px 18px; border-radius: 16px; }
    .sh-hero { padding: 32px 22px; }
    .sh-hero-title { font-size: 22px; }
    .sh-hero-sub { font-size: 14px; }
    .sh-bullets { gap: 8px; }
    .sh-card-head-sticky { position: static; }
    .sh-orders-toolbar { flex-direction: column; align-items: stretch; gap: 10px; }
    .sh-orders-toolbar .sh-btn-primary { justify-content: center; }
    .sh-order { flex-direction: column; gap: 10px; padding: 14px; }
    .sh-order-meta { gap: 10px; font-size: 11.5px; }
    .sh-connected-actions { flex-wrap: wrap; }
    .sh-connected-actions button { flex: 1; justify-content: center; }
    .sh-form-actions { flex-direction: column-reverse; }
    .sh-form-actions button { width: 100%; justify-content: center; }
  }
  @media (max-width: 480px) {
    .sh-balance-chip { padding: 6px 12px; gap: 8px; }
    .sh-balance-val { font-size: 15px; }
    .sh-hero-icon { width: 60px; height: 60px; font-size: 28px; border-radius: 16px; }
    .sh-hero-title { font-size: 20px; }
    .sh-steps li { gap: 12px; }
    .sh-step-num { width: 30px; height: 30px; font-size: 13px; }
    .sh-step-body strong { font-size: 15px; }
    .sh-step-body p { font-size: 13px; }
    .sh-scopes code { font-size: 11px; padding: 3px 7px; }
    .sh-connected-domain { font-size: 16px; }
    .sh-connected-meta { gap: 8px; font-size: 11px; }
  }
`;

export default ShopifyIntegration;
